import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@mondivago/supabase';
import type {
  SupportTicket,
  SupportTicketInput,
} from '@mondivago/types';

interface UseSupportTicketsResult {
  tickets: SupportTicket[];
  loading: boolean;
  error: string | null;
  fetchTickets: () => Promise<void>;
  createTicket: (input: SupportTicketInput) => Promise<{ success: boolean; error?: string; ticketId?: string }>;
  updateTicket: (id: string, patch: { status?: SupportTicket['status']; admin_reply?: string }) => Promise<{ success: boolean; error?: string }>;
}

/**
 * Shared hook for submitting support tickets to the Mondivago admin team.
 * Works for both suppliers and travelers — the backend resolves the submitter role.
 */
export function useSupportTickets(): UseSupportTicketsResult {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const { data, error: fnError } = await supabase.functions.invoke('api/support/tickets', {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      setTickets((data?.tickets ?? []) as SupportTicket[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  const createTicket = useCallback(async (input: SupportTicketInput) => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const { data, error: fnError } = await supabase.functions.invoke('api/support/ticket', {
        method: 'POST',
        body: input,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      return { success: true, ticketId: data?.ticketId as string | undefined };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to submit ticket.';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const updateTicket = useCallback(async (id: string, patch: { status?: SupportTicket['status']; admin_reply?: string }) => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const { data, error: fnError } = await supabase.functions.invoke(`api/support/ticket/${id}`, {
        method: 'PATCH',
        body: patch,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to update ticket.';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  // Load the current user's tickets on mount so badges (e.g. sidebar) have a count.
  useEffect(() => {
    void fetchTickets();
  }, [fetchTickets]);

  return {
    tickets,
    loading,
    error,
    fetchTickets,
    createTicket,
    updateTicket,
  };
}
